import { Text } from '@/components/Typography';
import { Link } from '@inertiajs/react';
import clsx from 'clsx';
import { LucideIcon } from 'lucide-react';
import React from 'react';

interface MenuItemProps {
    id: string;
    label: string;
    icon: LucideIcon;
    isActive: boolean;
    href: string;
    onClick: (id: string) => void;
    isCollapsed?: boolean;
    disabled?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({
    id,
    label,
    icon: Icon,
    isActive,
    href,
    onClick,
    isCollapsed = false,
    disabled = false,
}) => {
    const isExternalLink =
        href.startsWith('http://') || href.startsWith('https://');
    const menuItemClassName = clsx(
        'group',
        isActive ? 'menu-item-active' : 'menu-item-inactive',
        isCollapsed && 'justify-center px-3'
    );

    const menuItemTitle = isCollapsed ? label : undefined;

    if (disabled) {
        return (
            <span
                className={clsx(
                    menuItemClassName,
                    'cursor-not-allowed opacity-60 hover:!bg-transparent hover:!text-inherit'
                )}
                title={menuItemTitle ?? label}
                aria-disabled="true"
            >
                <Icon size={18} className="shrink-0" />
                {!isCollapsed && <Text as="span" className="font-medium">{label}</Text>}
            </span>
        );
    }

    if (href === '#') {
        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    onClick(id);
                }}
                className={menuItemClassName}
                title={menuItemTitle}
                aria-label={label}
            >
                <Icon size={18} className="shrink-0" />
                {!isCollapsed && <Text as="span" className="font-medium">{label}</Text>}
            </a>
        );
    }

    if (isExternalLink) {
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onClick(id)}
                className={menuItemClassName}
                title={menuItemTitle}
                aria-label={label}
            >
                <Icon size={18} className="shrink-0" />
                {!isCollapsed && <Text as="span" className="font-medium">{label}</Text>}
            </a>
        );
    }

    return (
        <Link
            href={href}
            onClick={() => onClick(id)}
            className={menuItemClassName}
            title={menuItemTitle}
            aria-label={label}
        >
            <Icon size={18} className="shrink-0" />
            {!isCollapsed && <Text as="span" className="font-medium">{label}</Text>}
        </Link>
    );
};

export default MenuItem;
